import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, Filter, CheckCircle2, Clock, AlertCircle,
  ChevronRight, ArrowUpDown, XCircle, Wrench
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter
} from "../components/ui/sheet";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const statusConfig = {
  open: { labelKey: "ca.status.open", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", icon: AlertCircle },
  in_progress: { labelKey: "ca.status.in_progress", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400", icon: Clock },
  closed: { labelKey: "ca.status.closed", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", icon: CheckCircle2 },
  overdue: { labelKey: "ca.status.overdue", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", icon: XCircle }
};

const priorityConfig = {
  low: { labelKey: "low", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  medium: { labelKey: "medium", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
  high: { labelKey: "high", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
  critical: { labelKey: "critical", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" }
};

const sourceOptions = [
  { value: "audit", label: "Audit" },
  { value: "risk_review", label: "Risk Review" },
  { value: "incident", label: "Incident" },
  { value: "management_review", label: "Management Review" },
  { value: "other", label: "Other" }
];

const CorrectiveActions = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [actions, setActions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCA, setSelectedCA] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [newCA, setNewCA] = useState({
    source: "audit",
    source_id: "",
    description: "",
    root_cause: "",
    action_plan: "",
    owner: "",
    due_date: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchActions();
    fetchSummary();
  }, [token]);

  const fetchActions = async () => {
    try {
      const response = await axios.get(`${API}/corrective-actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActions(response.data);
    } catch (error) {
      console.error("Failed to fetch corrective actions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/corrective-actions/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  };

  const createAction = async () => {
    if (!newCA.description) {
      toast.error(t("ca.toast.desc_required"));
      return;
    }
    try {
      await axios.post(`${API}/corrective-actions`, newCA, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("ca.toast.created"));
      setShowCreate(false);
      setNewCA({ source: "audit", source_id: "", description: "", root_cause: "", action_plan: "", owner: "", due_date: "", priority: "medium" });
      fetchActions();
      fetchSummary();
    } catch (error) {
      toast.error(extractError(error, t("ca.toast.create_failed")));
    }
  };

  const updateStatus = async (caId, status) => {
    try {
      await axios.put(`${API}/corrective-actions/${caId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("ca.toast.status_updated"));
      fetchActions();
      fetchSummary();
      if (selectedCA?.ca_id === caId) {
        setSelectedCA(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error(extractError(error, t("ca.toast.status_failed")));
    }
  };

  const updateEffectiveness = async (caId, review) => {
    try {
      await axios.put(`${API}/corrective-actions/${caId}/effectiveness?review=${encodeURIComponent(review)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("ca.toast.effectiveness_saved"));
      fetchActions();
    } catch (error) {
      toast.error(extractError(error, t("ca.toast.effectiveness_failed")));
    }
  };

  const filtered = actions.filter(a => {
    if (filterStatus !== "all" && a.status !== filterStatus) return false;
    if (filterPriority !== "all" && a.priority !== filterPriority) return false;
    if (searchQuery && !a.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const closureRate = summary?.total > 0
    ? Math.round((summary.by_status?.closed || 0) / summary.total * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading corrective actions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="corrective-actions-page">
      <PageHeader
        title={t("ca.title")}
        description={t("ca.subtitle")}
        actions={
          <Button onClick={() => setShowCreate(true)} data-testid="create-ca-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t("ca.new")}
          </Button>
        }
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-heading font-bold">{summary?.total || 0}</p>
            <p className="text-xs text-muted-foreground">{t("ca.total")}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-heading font-bold text-blue-500">{summary?.by_status?.open || 0}</p>
            <p className="text-xs text-muted-foreground">{t("ca.open")}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-heading font-bold text-yellow-500">{summary?.by_status?.in_progress || 0}</p>
            <p className="text-xs text-muted-foreground">{t("in_progress")}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-heading font-bold text-red-500">{summary?.by_status?.overdue || 0}</p>
            <p className="text-xs text-muted-foreground">{t("ca.overdue")}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <p className="text-2xl font-heading font-bold text-green-500">{closureRate}%</p>
            </div>
            <p className="text-xs text-muted-foreground">{t("ca.closure_rate")}</p>
            <Progress value={closureRate} className="h-1.5 mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("ca.search_ph")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="ca-search-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="ca-filter-status">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("ca.all_statuses")}</SelectItem>
            <SelectItem value="open">{t("ca.status.open")}</SelectItem>
            <SelectItem value="in_progress">{t("ca.status.in_progress")}</SelectItem>
            <SelectItem value="closed">{t("ca.status.closed")}</SelectItem>
            <SelectItem value="overdue">{t("ca.status.overdue")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-40" data-testid="ca-filter-priority">
            <SelectValue placeholder={t("priority")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("ca.all_priorities")}</SelectItem>
            <SelectItem value="critical">{t("critical")}</SelectItem>
            <SelectItem value="high">{t("high")}</SelectItem>
            <SelectItem value="medium">{t("medium")}</SelectItem>
            <SelectItem value="low">{t("low")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Actions List */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {filtered.map((ca, index) => {
            const StatusIcon = statusConfig[ca.status]?.icon || AlertCircle;
            return (
              <motion.div
                key={ca.ca_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
              >
                <Card
                  className="border-border hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedCA(ca)}
                  data-testid={`ca-card-${ca.ca_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge variant="outline" className={statusConfig[ca.status]?.color}>
                            {t(statusConfig[ca.status]?.labelKey || ca.status)}
                          </Badge>
                          <Badge variant="outline" className={priorityConfig[ca.priority]?.color}>
                            {t(priorityConfig[ca.priority]?.labelKey)}
                          </Badge>
                          <Badge variant="secondary" className="capitalize">{ca.source.replace("_", " ")}</Badge>
                        </div>
                        <p className="font-medium text-sm mb-1 line-clamp-2">{ca.description}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {ca.owner && <span>Owner: {ca.owner}</span>}
                          {ca.due_date && <span>Due: {new Date(ca.due_date).toLocaleDateString()}</span>}
                          <span>Created: {new Date(ca.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {actions.length === 0 ? "No corrective actions yet" : "No actions match your filters"}
            </p>
            {actions.length === 0 && (
              <Button className="mt-4" onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Action
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Create Sheet */}
      <Sheet open={showCreate} onOpenChange={setShowCreate}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("ca.new_title")}</SheetTitle>
            <SheetDescription>{t("ca.new_desc")}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("ca.source_label")} *</Label>
                <Select value={newCA.source} onValueChange={(v) => setNewCA({ ...newCA, source: v })}>
                  <SelectTrigger data-testid="ca-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{t(`ca.source.${s.value}`) || s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("ca.priority_label")}</Label>
                <Select value={newCA.priority} onValueChange={(v) => setNewCA({ ...newCA, priority: v })}>
                  <SelectTrigger data-testid="ca-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("low")}</SelectItem>
                    <SelectItem value="medium">{t("medium")}</SelectItem>
                    <SelectItem value="high">{t("high")}</SelectItem>
                    <SelectItem value="critical">{t("critical")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("ca.desc_label")} *</Label>
              <Textarea
                value={newCA.description}
                onChange={(e) => setNewCA({ ...newCA, description: e.target.value })}
                placeholder={t("description")}
                rows={3}
                data-testid="ca-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("ca.root_cause")}</Label>
              <Textarea
                value={newCA.root_cause}
                onChange={(e) => setNewCA({ ...newCA, root_cause: e.target.value })}
                placeholder={t("description")}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("ca.action_plan")}</Label>
              <Textarea
                value={newCA.action_plan}
                onChange={(e) => setNewCA({ ...newCA, action_plan: e.target.value })}
                placeholder={t("description")}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("ca.owner_label")}</Label>
                <Input
                  value={newCA.owner}
                  onChange={(e) => setNewCA({ ...newCA, owner: e.target.value })}
                  placeholder={t("owner")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("ca.due_date_label")}</Label>
                <Input
                  type="date"
                  value={newCA.due_date}
                  onChange={(e) => setNewCA({ ...newCA, due_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel")}</Button>
            <Button onClick={createAction} data-testid="save-ca-btn">{t("ca.create_btn")}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Detail Dialog */}
      <CADetailDialog
        ca={selectedCA}
        onClose={() => setSelectedCA(null)}
        onStatusChange={updateStatus}
        onEffectivenessReview={updateEffectiveness}
      />
    </div>
  );
};

const CADetailDialog = ({ ca, onClose, onStatusChange, onEffectivenessReview }) => {
  const { t } = useI18n();
  const [effectivenessInput, setEffectivenessInput] = useState("");

  if (!ca) return null;

  return (
    <Dialog open={!!ca} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge variant="outline" className={statusConfig[ca.status]?.color}>
              {t(statusConfig[ca.status]?.labelKey || ca.status)}
            </Badge>
            <Badge variant="outline" className={priorityConfig[ca.priority]?.color}>
              {t(priorityConfig[ca.priority]?.labelKey)}
            </Badge>
            <Badge variant="secondary" className="capitalize">{t(`ca.source.${ca.source}`) || ca.source.replace("_", " ")}</Badge>
          </div>
          <DialogTitle className="text-lg">{t("ca.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* Status Actions */}
          <div className="flex items-center gap-2">
            <Label className="text-sm">{t("status")}:</Label>
            <Select onValueChange={(v) => onStatusChange(ca.ca_id, v)}>
              <SelectTrigger className="w-40" data-testid="ca-detail-status-select">
                <SelectValue placeholder={t(statusConfig[ca.status]?.labelKey || ca.status)} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">{t("ca.status.in_progress")}</SelectItem>
                <SelectItem value="closed">{t("ca.status.closed")}</SelectItem>
                <SelectItem value="overdue">{t("ca.status.overdue")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label className="text-sm text-muted-foreground">{t("description")}</Label>
            <p className="mt-1 text-sm">{ca.description}</p>
          </div>

          {/* Root Cause */}
          {ca.root_cause && (
            <div>
              <Label className="text-sm text-muted-foreground">{t("ca.root_cause")}</Label>
              <p className="mt-1 text-sm">{ca.root_cause}</p>
            </div>
          )}

          {/* Action Plan */}
          {ca.action_plan && (
            <div>
              <Label className="text-sm text-muted-foreground">{t("ca.action_plan")}</Label>
              <p className="mt-1 text-sm">{ca.action_plan}</p>
            </div>
          )}

          {/* Meta */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-xs text-muted-foreground">Owner</Label>
              <p>{ca.owner || "Unassigned"}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Due Date</Label>
              <p>{ca.due_date ? new Date(ca.due_date).toLocaleDateString() : "Not set"}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Created</Label>
              <p>{new Date(ca.created_at).toLocaleDateString()}</p>
            </div>
            {ca.closed_at && (
              <div>
                <Label className="text-xs text-muted-foreground">Closed</Label>
                <p>{new Date(ca.closed_at).toLocaleDateString()}</p>
              </div>
            )}
          </div>

          {/* Effectiveness Review */}
          <div>
            <Label className="text-sm font-medium">Effectiveness Review</Label>
            {ca.effectiveness_review ? (
              <p className="mt-1 text-sm p-3 rounded-lg bg-muted/30">{ca.effectiveness_review}</p>
            ) : (
              <div className="mt-2 space-y-2">
                <Textarea
                  value={effectivenessInput}
                  onChange={(e) => setEffectivenessInput(e.target.value)}
                  placeholder="Evaluate if the corrective action was effective..."
                  rows={3}
                  data-testid="ca-effectiveness-input"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (effectivenessInput) {
                      onEffectivenessReview(ca.ca_id, effectivenessInput);
                      setEffectivenessInput("");
                    }
                  }}
                  data-testid="ca-save-effectiveness-btn"
                >
                  Save Review
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectiveActions;
